import { message } from "antd";
import { type History } from "history";
import {
    type AttachedFiles,
    AttachedFilesRequest,
    type BTFileSystem,
} from "legacyComponents/FileUploadContainer.types";
import memoizeOne from "memoize-one";
import moment, { type Moment } from "moment";
import queryString from "query-string";

import { SignatureRequestStatus } from "@btnet/services-btnet/handlers/models/signatureRequestStatus";
import { mobile } from "@btnet/utilities-webview";

import {
    DirectoryType,
    DiscussionsLinkedTypes,
    DocumentInstanceType,
    FileEncryption,
    FileLocation,
    FileStatus,
    type MediaPropertiesTab,
    MediaType,
    ShareType,
    TempFileTypes,
} from "types/enum";
import {
    attachedDocsFolderId,
    attachedPhotosFolderId,
    attachedVideosFolderId,
    maxDaysInTrash,
    needsYourSignature,
    ownerDocumentsFolderId,
    QueryStringParamNames,
    rootLevelFolderId,
    subUploadFolders,
    trashFolderId,
} from "types/MediaConstants";

import { showAPIErrorMessage } from "utilities/apiHandler";
import { getBase64ImageFromUrl, getFileExtension, validateBase64Image } from "utilities/file/file";
import { type GridSort, type ISortableColumn } from "utilities/list/list.types";
import { isInPortal } from "utilities/portal/portal";
import { getRelatedItemLink } from "utilities/relatedItem/relatedItem";
import { addPathForCurrentPortal, routes } from "utilities/routes";
import { dateSorter, lexicographicSorter, numberSorter } from "utilities/sort/sort";
import { isNullOrWhitespace } from "utilities/string/string";

import { canViewBTFileSystemInLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { type IFileShareHandler } from "commonComponents/btWrappers/BTSocialMediaButtons/FileShare.api.handler";
import { type IGetShareTokenRequest } from "commonComponents/btWrappers/BTSocialMediaButtons/FileShare.types";
import { type BTServiceFileUpload } from "commonComponents/entity/media/AttachmentEntityPreview/AttachmentEntity.api.types";
import { type IMediaPropertiesEntity } from "commonComponents/entity/media/MediaProperties/MediaProperties.api.types";
import { RelatedItemType } from "commonComponents/entity/relatedItem/RelatedItem.types";
import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";

import { TemplateType } from "entity/document/Document/Document.api.types";
import {
    type AssociatedEntityInfo,
    type BreadCrumbInformation,
    type BTFile,
    type BTFolder,
    type IFolderMenuItemsVisibility,
    type IMediaListSort,
    type MediaEntry,
    type MediaListEntity,
} from "entity/media/common/mediaTypes";

export function getMediaString(mediaType: MediaType) {
    switch (mediaType) {
        case MediaType.Document:
            return "Files";
        case MediaType.Photo:
            return "Photos";
        case MediaType.Video:
            return "Videos";
        case MediaType.Unavailable:
            return "Unavailable";
        case MediaType.All:
            return "All";
    }
}

export function getDocumentInstanceTypeString(id: DocumentInstanceType): string {
    switch (id) {
        case DocumentInstanceType.ChangeOrder:
            return "Change Order";
        case DocumentInstanceType.SelectionChoice:
            return "Selection Choice";
        case DocumentInstanceType.Selection:
            return "Selection";
        case DocumentInstanceType.Lead:
            return "Lead";
        case DocumentInstanceType.PurchaseOrder:
            return "Purchase Order";
        case DocumentInstanceType.LeadEmailAttachment:
            return "Message";
        case DocumentInstanceType.BidPackage:
            return "Bid Package";
        case DocumentInstanceType.Bid:
            return "Bid";
        case DocumentInstanceType.MessagesEmailAttachment:
            return "Message";
        case DocumentInstanceType.ScheduleItems:
            return "Schedule Item";
        case DocumentInstanceType.CustomerInvoices:
            return "Invoice";
        case DocumentInstanceType.LeadProposal:
            return "Lead Proposal";
        case DocumentInstanceType.DailyLog:
            return "Daily Log";
        case DocumentInstanceType.Estimate:
            return "Estimate";
        case DocumentInstanceType.ToDo:
            return "To-Do";
        case DocumentInstanceType.LienWaiver:
            return "Lien Waiver";
        case DocumentInstanceType.ProposalFormatItem:
            return "Group";
        case DocumentInstanceType.Warranty:
            return "Warranty";
        case DocumentInstanceType.WarrantyService:
            return "Warranty Service";
        case DocumentInstanceType.RFI:
            return "RFI";
        case DocumentInstanceType.RFIResponse:
            return "RFI Response";
        case DocumentInstanceType.TodoChecklistItem:
            return "To-Do Checklist Item";
        case DocumentInstanceType.CreditMemo:
            return "Credit Memo";
        case DocumentInstanceType.Deposit:
            return "Deposit";
        case DocumentInstanceType.Receipt:
            return "Receipt";
        case DocumentInstanceType.Takeoff:
            return "Takeoff";
        case DocumentInstanceType.Bill:
            return "Bill";
        case DocumentInstanceType.Task:
            return "Task";
        case DocumentInstanceType.ProposalAgreement:
            return "Proposal";
        case DocumentInstanceType.Submittal:
            return "Submittal";
        case DocumentInstanceType.SubVendor:
            return "Sub/Vendor";
        case DocumentInstanceType.Specification:
            return "Spec";
        default:
            return "Entity";
    }
}

export function getDirectoryTypeName(directoryType: DirectoryType) {
    switch (directoryType) {
        case DirectoryType.Standard:
            return "Standard";
        case DirectoryType.GlobalDocs:
            return "Global Documents";
        case DirectoryType.AttachedFiles:
            return "Attached Files";
        case DirectoryType.SubOwnerUploadedFiles:
            return "Sub/Owner Uploaded Files";
        case DirectoryType.Trash:
            return "Trash";
        case DirectoryType.UnsignedDocuments:
            return "Documents to Sign";
        default:
            return "Unknown";
    }
}

export function getListLinkForMediaType(
    destinationFolderId: number,
    mediaType: MediaType,
    dirType: DirectoryType,
) {
    let listLink = "";
    switch (mediaType) {
        case MediaType.Document:
            listLink = routes.document.getListLink(dirType, destinationFolderId);
            break;
        case MediaType.Photo:
            listLink = routes.photo.getListLink(dirType, destinationFolderId);
            break;
        case MediaType.Video:
            listLink = routes.video.getListLink(dirType, destinationFolderId);
            break;
        default:
            throw new Error("Unsupported MediaType for Go To Folder");
    }

    return listLink;
}

export function handleGoToFolder(
    destinationFolderId: number,
    mediaType: MediaType,
    history: History,
    dirType: DirectoryType = DirectoryType.Standard,
) {
    const newUrl = getListLinkForMediaType(destinationFolderId, mediaType, dirType);
    history.push(newUrl);
}

export function getLinkToFolder(destinationFolderId: number, mediaType: MediaType, jobId: number) {
    const dirType = jobId > 0 ? DirectoryType.Standard : DirectoryType.GlobalDocs;

    return getListLinkForMediaType(destinationFolderId, mediaType, dirType);
}

export const getCurrentDirectoryType = (
    breadCrumbs?: BreadCrumbInformation[] | null,
): DirectoryType => {
    let directoryType = DirectoryType.Standard;
    if (breadCrumbs && breadCrumbs.length > 0) {
        directoryType = breadCrumbs[breadCrumbs.length - 1].directoryType;
    }

    return directoryType;
};

export const getCurrentAssociatedType = (
    breadCrumbs?: BreadCrumbInformation[] | null,
): DocumentInstanceType | null => {
    if (breadCrumbs && breadCrumbs.length > 0) {
        return breadCrumbs[breadCrumbs.length - 1].associatedType;
    }

    return null;
};

export function getFileUrlFormatted(docInstanceId: number, extension: string, url: string) {
    // Only want to set the owner viewed url params on non-photo types,
    // because the media modal will handle setting owner viewed
    if (isInPortal({ owner: true }) && !isPhotoFileType(extension)) {
        const newUrl = new URL(url);
        newUrl.searchParams.append(QueryStringParamNames.IsOwner, "true");
        newUrl.searchParams.append(
            QueryStringParamNames.DocumentInstanceId,
            docInstanceId.toString(),
        );
        newUrl.searchParams.append(
            QueryStringParamNames.Encryption,
            FileEncryption.FileIdIsUnencrypted.toString(),
        );
        return newUrl.href;
    }
    return url;
}

export function isPDFFileType(extension: string) {
    if (!extension) {
        // Even though the extension param is a string, the extension can be undefined when it is not set in the constructor of a BTFileSystem
        return false;
    }

    return extension.replace(/\./g, "").toLowerCase() === "pdf";
}

export const photoFileTypes = [
    "jpg",
    "jpeg",
    "bmp",
    "gif",
    "png",
    "heic",
    "heif",
    "ai",
    "svg",
    "eps",
    "tif",
    "tiff",
    "webp",
];

/**
 * This method checks if the provided extension is present in the photoFileTypes array
 * @param extension
 * @returns Bool indicating if the provided extension is present in the photoFileTypes array
 */
export function isPhotoFileType(extension: string | null) {
    if (!extension) {
        return false;
    }
    return photoFileTypes.includes(extension.replace(/\./g, "").toLowerCase());
}

/**
 * Photo formats we accept for upload that the Nutrient Web SDK cannot render — they require
 * Nutrient's server-side Document Engine, which we don't run. Browsers decode all four natively,
 * so Photo details falls back to the plain <img> preview instead of the Nutrient draw surface.
 *
 * HEIC/HEIF are also Nutrient-unsupported but browsers can't decode them either, so falling back
 * wouldn't help — they stay on the file-icon + download path.
 */
export const photoDrawFallbackTypes = ["webp", "svg", "gif", "bmp"];

/**
 * This method checks if the provided extension is a photo format that must fall back from the
 * Nutrient draw experience to the plain image preview
 * @param extension
 * @returns Bool indicating if the provided extension is present in the photoDrawFallbackTypes array
 */
export function isPhotoDrawFallbackType(extension: string | null | undefined) {
    if (!extension) {
        return false;
    }
    return photoDrawFallbackTypes.includes(extension.replace(/\./g, "").toLowerCase());
}

export const videoFileTypes = ["wmv", "mp4", "mpg", "mpeg", "mov", "m4v", "avi"];

/**
 * This method checks if the provided extension is present in the videoFileTypes array
 * @param extension
 * @returns Bool indicating if the provided extension is present in the videoFileTypes array
 */
export function isVideoFileType(extension: string) {
    return videoFileTypes.includes(extension.replace(/\./g, "").toLowerCase());
}
export function isFileAVideo(file: File) {
    const fileExtension = getFileExtension(file.name);
    return file.type.includes("video") || isVideoFileType(fileExtension);
}

export const documentFileTypes = [
    "pdf",
    "m4a",
    "wps",
    "xps",
    "odt",
    "doc",
    "docx",
    "docm",
    "dotx",
    "one",
    "rtf",
    "xls",
    "xlsx",
    "xltx",
    "xlm",
    "xlsm",
    "pps",
    "ppt",
    "pptx",
    "vis",
    "mdb",
    "zip",
    "rar",
    "skp",
    "csv",
    "txt",
    "html",
    "htm",
    "pub",
    "spv",
    "wav",
    "dwg",
    "dwf",
    "dwfx",
    "dxf",
    "bak",
    "est",
    "3gp",
    "3gpp",
    "blg",
    "esx",
    "dat",
    "numbers",
    "pages",
    "3d",
    "2d",
    "tcp",
    "layout",
    "plan",
    "rvt",
    "bp",
    "jp2",
    "eps",
    "dsg",
    "ppsx",
    "deck",
    "mpp",
    "gsheet",
    "msg",
    "kit",
    "swiftjob",
    "ttx",
    "eml",
    "vsd",
    "au",
    "spd",
    "3dm",
    "pcs",
    "kmz",
    "kml",
    "bimx",
    "ec6",
    "bpn",
    "ods",
    "bld",
    "amr",
    "gsm",
    "pln",
    "cdr",
    "svg",
    "ai",
    "wpd",
    "wp",
    "wp7",
    "wp6",
    "wp5",
    "wp4",
    "ctb",
    "wrl",
    "fcp",
    "vsdx",
    "heic",
    "tlx",
    "json",
    "log",
    "kmz",
    "kml",
    "psd",
    "skb",
    "skp",
    "mmdl",
    "cad",
    "alt",
    "vwx",
    "atl",
    "lnf",
];

/**
 * This method checks if the provided extension is present in the documentFileTypes array
 * @param extension
 * @returns Bool indicating if the provided extension is present in the documentFileTypes array
 */
export function isDocumentFileType(extension: string) {
    return documentFileTypes.includes(extension.replace(/\./g, "").toLowerCase());
}

/**
 * This method checks if the provided extension is one shared by both Document and Photo file types
 * Currently the overlapping file types are: ai, eps, heic, and svg
 * @param extension
 * @returns Bool indicating if the provided extension is one of the overlapping Document/Photo file types
 */
export function isDocumentOrPhotoOverlapFileType(extension: string) {
    const documentPhotoOverlapFileTypes = documentFileTypes.filter((x) =>
        photoFileTypes.includes(x),
    );
    return documentPhotoOverlapFileTypes.includes(extension.replace(/\./g, "").toLowerCase());
}

export function isReadOnly(canAdd: boolean, canEdit: boolean, isNew: boolean) {
    return (!canEdit && !canAdd) || (!canEdit && canAdd && !isNew);
}

/**
 * Whether the current user may commit an annotation to this file.
 *
 * `canAnnotate` is file-scoped (for an attached file it follows the owning entity), unlike
 * `canEdit`/`canAdd`, which carry only the tab permission. Deliberately excludes the active
 * tab: the Save button must not disappear when the user switches to Version history, so the
 * tab check belongs on the annotation *toolbar*, not on the permission to save.
 *
 * Both the Save button and the submit guard must use this single predicate — when they
 * diverge, the button can be hidden while the dirty-tracking save-then-navigate path still
 * commits (and vice versa).
 *
 * Media-type agnostic on purpose. Documents reach annotation without the PhotoDraw flag, so
 * callers on the photo path AND the flag in themselves rather than folding a photo-only
 * rollout gate into a shared permission rule.
 */
export function canCommitAnnotation(
    canAnnotate: boolean | undefined,
    docLocation: FileLocation | undefined,
    hasSignatures: boolean,
) {
    return !!canAnnotate && docLocation !== FileLocation.AllBuilderShared && !hasSignatures;
}

/**
 * Photos populate `entity.extension`; documents populate `entity.selectedFile.ext`. Prefer the
 * canonical source for the mediaType, then fall back to the other field.
 *
 * Lives here rather than in MediaProperties so every caller of {@link getPhotoSaveRoute} can feed
 * it the same extension — the routing guarantee only holds if both sides read the entity alike.
 */
export function getResolvedExtension(entity: IMediaPropertiesEntity | undefined): string {
    if (!entity) {
        return "";
    }

    return entity.mediaType === MediaType.Photo
        ? entity.extension || entity.selectedFile?.ext || ""
        : entity.selectedFile?.ext || entity.extension || "";
}

/**
 * Whether to offer rotation on a media file. Rotation is an edit — it dirties the form and the
 * save is refused without edit permission — so a read-only user must not be offered it at all.
 *
 * Both renderers must read this: the plain `<img>` preview draws its own rotate buttons, and
 * Nutrient adds rotate tools to the DocumentViewer toolbar. Gating only one leaves the other
 * offering rotation to a user who cannot save it.
 */
export function canRotateMedia(
    canAdd: boolean | undefined,
    canEdit: boolean | undefined,
    isNew: boolean,
) {
    return !isReadOnly(canAdd ?? false, canEdit ?? false, isNew);
}

/** Which endpoint a media save posts to. See {@link getPhotoSaveRoute}. */
export const MediaSaveRoute = {
    Document: "document",
    LegacyPhoto: "legacyPhoto",
} as const;

export type MediaSaveRoutes = (typeof MediaSaveRoute)[keyof typeof MediaSaveRoute];

/**
 * Which endpoint a photo save will take. This is a routing fact, not a permission — keep the two
 * separate, and let callers combine them.
 *
 * It matters to permission checks because the two endpoints authorize differently: the legacy photo
 * endpoint requires Photos + Update, so file-scoped `canCommitAnnotation` must NOT authorize a save
 * that routes there or the user gets a guaranteed 403. The document endpoint has no such
 * requirement.
 *
 * Both the Save button and the submit guard must read this same helper — deriving the route
 * separately at each call site is how they drift. Feed it the same `extension` from both sides too:
 * use {@link getResolvedExtension} rather than reading an entity field directly.
 */
export function getPhotoSaveRoute(
    mediaType: MediaType,
    extension: string | undefined,
    hasReplacementFile: boolean,
): MediaSaveRoutes {
    if (mediaType !== MediaType.Photo) {
        return MediaSaveRoute.Document;
    }

    // Formats Nutrient can't render fall back to the plain preview, which offers rotation. The
    // document request has no rotation field, so a metadata-only save there has to take the legacy
    // endpoint to persist it. A file replacement always takes the document endpoint so the new
    // version is recorded — the legacy endpoint overwrites in place without a version row.
    if (isPhotoDrawFallbackType(extension) && !hasReplacementFile) {
        return MediaSaveRoute.LegacyPhoto;
    }

    return MediaSaveRoute.Document;
}

export function getDiscussionLinkType(mediaType: MediaType) {
    switch (mediaType) {
        case MediaType.Document:
        case MediaType.Unavailable:
        case MediaType.All:
            return DiscussionsLinkedTypes.Documents;
        case MediaType.Photo:
            return DiscussionsLinkedTypes.PhotoDocs;
        case MediaType.Video:
            return DiscussionsLinkedTypes.Videos;
    }
}

const templateFileTypes = ["doc", "docx", "xls", "xlsx"];
export function isTemplateFileType(extension: string) {
    return templateFileTypes.includes(extension.replace(/\./g, "").toLowerCase());
}

export function getBTFileRelatedItemLink(file: BTFile, associatedEntityInfo: AssociatedEntityInfo) {
    const { associatedType, relatedItemType } = associatedEntityInfo;

    // Takeoff related items will always have external URLs
    if (relatedItemType === RelatedItemType.Takeoff) {
        return associatedEntityInfo.externalUrl!;
    }

    let entityId = associatedEntityInfo.associatedEntityId;
    if (
        (associatedType === DocumentInstanceType.POPaymentsFileCustomField &&
            relatedItemType !== RelatedItemType.Bill) ||
        associatedType === DocumentInstanceType.RFIResponse ||
        associatedType === DocumentInstanceType.TodoChecklistItem ||
        associatedType === DocumentInstanceType.WarrantyService ||
        associatedType === DocumentInstanceType.SignedPurchaseOrderWithPayment ||
        associatedType === DocumentInstanceType.SignedPurchaseOrderWithoutPayment ||
        relatedItemType === RelatedItemType.PurchaseOrderPaymentLienWaiver
    ) {
        entityId = associatedEntityInfo.associatedParentId;
    } else if (relatedItemType === RelatedItemType.BillLienWaiver) {
        entityId = associatedEntityInfo.associatedGrandParentId;
    }

    return getRelatedItemLink(
        entityId,
        relatedItemType,
        file.jobId,
        associatedEntityInfo.externalId ?? undefined,
    );
}

/** Converts the provided docType to a TempFileType */
export function getTempFileTypeFromDocumentInstanceType(docType: DocumentInstanceType) {
    switch (docType) {
        case DocumentInstanceType.Bid:
            return TempFileTypes.Bids;
        case DocumentInstanceType.BidPackage:
            return TempFileTypes.BidPackages;
        case DocumentInstanceType.DailyLog:
            return TempFileTypes.DailyLogs;
        case DocumentInstanceType.Lead:
            return TempFileTypes.Leads;
        case DocumentInstanceType.LeadProposal:
            return TempFileTypes.LeadProposals;
        case DocumentInstanceType.MessagesEmailAttachment:
            return TempFileTypes.MessageAttachments;
        case DocumentInstanceType.PurchaseOrder:
        case DocumentInstanceType.SignedPurchaseOrderWithPayment:
            return TempFileTypes.PurchaseOrders;
        case DocumentInstanceType.RFI:
            return TempFileTypes.RFIs;
        case DocumentInstanceType.ToDo:
            return TempFileTypes.ToDos;
        case DocumentInstanceType.RFIResponse:
            return TempFileTypes.RFIResponse;
        case DocumentInstanceType.Selection:
            return TempFileTypes.Selections;
        case DocumentInstanceType.SelectionChoice:
            return TempFileTypes.SelectionChoices;
        case DocumentInstanceType.ChangeOrder:
            return TempFileTypes.ChangeOrderDocs;
        case DocumentInstanceType.Warranty:
        case DocumentInstanceType.WarrantyService:
            return TempFileTypes.Warranty;
        case DocumentInstanceType.ScheduleItems:
            return TempFileTypes.ScheduleItem;
        case DocumentInstanceType.ProposalFormatItem:
            return TempFileTypes.ProposalFormatItems;
        case DocumentInstanceType.LienWaiver:
        case DocumentInstanceType.SignedLienWaiver:
            return TempFileTypes.LienWaivers;
        case DocumentInstanceType.CustomerInvoices:
            return TempFileTypes.OwnerPayments;
        case DocumentInstanceType.Estimate:
            return TempFileTypes.Estimate;
        case DocumentInstanceType.StandaloneDocument:
            return TempFileTypes.StandaloneDocument;
        case DocumentInstanceType.LeadEmailAttachment:
            return TempFileTypes.LeadEmailAttachment;
        case DocumentInstanceType.TodoChecklistItem:
            return TempFileTypes.TodoChecklistItem;
        case DocumentInstanceType.CostCodeItems:
            return TempFileTypes.CostCodeItems;
        case DocumentInstanceType.CreditMemo:
            return TempFileTypes.CreditMemo;
        case DocumentInstanceType.Deposit:
            return TempFileTypes.Deposit;
        case DocumentInstanceType.Receipt:
            return TempFileTypes.Receipt;
        case DocumentInstanceType.Takeoff:
            return TempFileTypes.Takeoff;
        case DocumentInstanceType.Bill:
            return TempFileTypes.Bill;
        case DocumentInstanceType.Task:
            return TempFileTypes.Task;
        case DocumentInstanceType.ProposalAgreement:
            return TempFileTypes.ProposalAgreement;
        case DocumentInstanceType.SelectionOption:
            return TempFileTypes.SelectionOption;
        case DocumentInstanceType.SelectionCatalogOption:
            return TempFileTypes.SelectionCatalogOption;
        case DocumentInstanceType.Specification:
            return TempFileTypes.Specification;
        case DocumentInstanceType.Submittal:
            return TempFileTypes.Submittal;
        case DocumentInstanceType.SubVendor:
            return TempFileTypes.SubVendor;
        default:
            throw new Error(
                `media.utils: DocumentInstanceType ${docType} not supported! Please add your DocumentInstanceType to the function getTempFileTypeFromDocumentInstanceType()`,
            );
    }
}

export function downloadFromUrl(url: string, name: string) {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
}

export function openNewTabFromUrl(url: string) {
    const anchor = document.createElement("a");
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
    anchor.href = url;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
}

export function downloadBTFile(file: BTFile) {
    downloadFromUrl(file.downloadUrl, file.friendlyFileName);
}

export function getFolderLink(folder: BTFolder) {
    return getFolderLinkByDetails(folder.directoryType, folder.id, folder.mediaType);
}

/**
 * This method determines the appropriate file type based on the provided extension and media type
 * Media type is used to determine the most appropriate type in cases where the file extension is shared between Document and Photo file types
 * @param fileExtension
 * @param fileMediaType
 * @returns String representing file type; one of "Document", "Photo", "Video", or "Other"
 */
export function getFileTypeByExtension(fileExtension: string, fileMediaType: MediaType) {
    // Some overlap exists between document and photo file types
    // This handles cases where the file is one of overlapping types; media type is used to determine which type is most appropriate in these scenarios
    // All other cases are handled by just the extension
    if (isDocumentOrPhotoOverlapFileType(fileExtension)) {
        switch (fileMediaType) {
            case MediaType.Document:
                return "Document";
            case MediaType.Photo:
                return "Photo";
            default:
                break;
        }
    }

    if (isDocumentFileType(fileExtension)) {
        return "Document";
    } else if (isPhotoFileType(fileExtension)) {
        return "Photo";
    } else if (isVideoFileType(fileExtension)) {
        return "Video";
    } else {
        return "Other";
    }
}

export function getFolderLinkByDetails(
    directoryType: DirectoryType,
    folderId: number,
    mediaType: MediaType,
) {
    switch (mediaType) {
        case MediaType.Document:
            return routes.document.getListLink(directoryType, folderId);
        case MediaType.Photo:
            return routes.photo.getListLink(directoryType, folderId);
        case MediaType.Video:
            return routes.video.getListLink(directoryType, folderId);
        default:
            return "";
    }
}

export function isRootLevelFolder(folderId?: number) {
    return folderId === rootLevelFolderId;
}

export function isGlobalDocsJob(jobId: number) {
    return jobId === JobIdTypes.GlobalDocs;
}

export function canSelectFolder(folder: BTFolder) {
    const { isSpecialFolder, folderAssociatedType } = folder.specialFolderExtraData;
    return !isSpecialFolder || folderAssociatedType !== DocumentInstanceType.StandaloneDocument;
}

/**
 * Native HTML5 drag on media rows/cards exists only to move an item into a folder. The mobile
 * webview exposes no reachable drop target for that, but iOS WKWebView still lifts any
 * `draggable` element on long-press — so the user gets a drag that visibly does nothing and
 * reads as broken sorting (ado-286243). Photo reorder is unaffected: it uses SortableJS and
 * grabs by an explicit handle, not this attribute.
 */
function canDragMediaInCurrentContext() {
    return !mobile.isWebview;
}

export function canDragFolderForAllMediaTypes(folder: BTFolder) {
    return (
        canDragMediaInCurrentContext() &&
        folder.canUpdate &&
        folder.parentFolderId !== rootLevelFolderId
    );
}

export function canDragFileForAllMediaType(file: BTFile, directoryType: DirectoryType) {
    return (
        canDragMediaInCurrentContext() &&
        file.permissions.canMove &&
        directoryType !== DirectoryType.SubOwnerUploadedFiles
    );
}

export function getMediaListEmptyStateTitle(
    mediaType: MediaType,
    isRootFolder: boolean,
    directoryType?: DirectoryType,
): string {
    if (directoryType === DirectoryType.Trash) {
        return "Nothing in Trash";
    } else if (isRootFolder) {
        return "Add a folder";
    }
    switch (mediaType) {
        case MediaType.Document:
            return "Add a document";
        case MediaType.Photo:
            return "Add a photo";
        case MediaType.Video:
            return "Add a video";
        default:
            return "Add a file";
    }
}

export function areJobAndDirectoryTypeMatching(jobId: number, directoryType: DirectoryType) {
    if (directoryType === DirectoryType.GlobalDocs) {
        return true;
    }
    return !(jobId === JobIdTypes.GlobalDocs && directoryType === DirectoryType.Standard);
}

export function isGlobalDocsTrash(directoryType: DirectoryType, folderId: number) {
    return directoryType === DirectoryType.GlobalDocs && folderId === trashFolderId;
}

export function isTrashView(directoryType: DirectoryType, folderId: number) {
    return directoryType === DirectoryType.Trash || isGlobalDocsTrash(directoryType, folderId);
}

export function isDirectoryAlwaysReadonly(directoryType: DirectoryType) {
    return (
        directoryType === DirectoryType.AttachedFiles ||
        directoryType === DirectoryType.Trash ||
        (directoryType === DirectoryType.SubOwnerUploadedFiles && isInPortal({ builder: true }))
    );
}

/**
 * Removes only the specified extension from the provided filename. Case insensitive.
 * @param fileName
 * @param extension
 * @returns trimmed filename
 */
export function trimOffSpecificExtension(fileName: string, extension: string | null) {
    if (isNullOrWhitespace(extension)) {
        return fileName;
    }

    return fileName.replace(new RegExp(`\\.${extension}$`, "i"), "");
}

/**
 * This method will add the provided extension to the filename if it does not already exist. If the file name already includes an extension it will not add the extension.
 * @param fileName Name of file, can include or not include extension
 * @param extension Extension of the file
 * @returns File name with extension appended
 */
export function addExtensionToFileName(fileName: string, extension: string) {
    if (isNullOrWhitespace(extension)) {
        return fileName;
    }

    // Removes periods from extension if there are any
    const formattedExtension = extension.replace(/\.+/g, "").toLowerCase();

    const regex = new RegExp(`\\.${formattedExtension}$`, "i");

    // File name already has extension, no need to add
    if (regex.exec(fileName)) {
        return fileName;
    }

    return `${fileName}.${formattedExtension}`;
}

export function getMediaListEmptyStateDescription(
    mediaType: MediaType,
    isRootFolder: boolean,
    directoryType?: DirectoryType,
    canAddFiles?: boolean,
): string {
    if (directoryType && directoryType === DirectoryType.Trash) {
        return `Move items you don't need to trash. Items in trash will be deleted forever after ${maxDaysInTrash} days.`;
    } else if (directoryType && isDirectoryAlwaysReadonly(directoryType)) {
        return "This folder is empty.";
    } else if (isRootFolder) {
        return `Organize your documents, photos, and videos. Add a folder to start uploading files.`;
    } else if (
        isInPortal({ subs: true, owner: true }) &&
        directoryType !== DirectoryType.SubOwnerUploadedFiles &&
        !canAddFiles
    ) {
        return `This folder is either empty or you don't have permission to view the content. Reach out to the account admin for more info.`;
    } else if (mediaType === MediaType.Document) {
        return "Upload, annotate, and sign documents related to the job. Add a document to get started.";
    } else if (mediaType === MediaType.Photo) {
        return "Upload, annotate, and organize photos related to the job. Add a photo to get started.";
    } else if (mediaType === MediaType.Video) {
        return "Upload, annotate, and organize videos related to the job. Add a video to get started.";
    } else {
        return `This folder is either empty or you don't have permission to view the content. Add ${getMediaString(
            mediaType,
        ).toLowerCase()} by clicking the "Upload" button, or drop them onto the page to begin uploading.`;
    }
}

export enum MediaSortColumn {
    Name = "Name",
    TotalSize = "Total Size",
    Modified = "Modified",
    Created = "Created",
    Uploaded = "Uploaded",
    Duration = "Duration",
    DateTaken = "Date Taken",
    DateDeleted = "Date Deleted",
}

const folderSortableColumns = [
    { id: MediaSortColumn.Name, isSortable: true, name: MediaSortColumn.Name },
    { id: MediaSortColumn.Created, isSortable: true, name: MediaSortColumn.Created },
    { id: MediaSortColumn.Modified, isSortable: true, name: MediaSortColumn.Modified },
];

const fileSortableColumns = [
    { id: MediaSortColumn.Name, isSortable: true, name: MediaSortColumn.Name },
    { id: MediaSortColumn.TotalSize, isSortable: true, name: MediaSortColumn.TotalSize },
    { id: MediaSortColumn.Duration, isSortable: true, name: MediaSortColumn.Duration },
    { id: MediaSortColumn.Uploaded, isSortable: true, name: MediaSortColumn.Uploaded },
    { id: MediaSortColumn.Modified, isSortable: true, name: MediaSortColumn.Modified },
    { id: MediaSortColumn.DateTaken, isSortable: true, name: MediaSortColumn.DateTaken },
];

export const getFolderSortableColumns = (directoryType: DirectoryType) => {
    if (directoryType === DirectoryType.AttachedFiles) {
        return folderSortableColumns.filter((x) => x.name !== MediaSortColumn.Created);
    }
    return folderSortableColumns;
};

export const getFileSortableColumns = (directoryType: DirectoryType, mediaType: MediaType) => {
    if (directoryType === DirectoryType.Trash) {
        return [
            {
                id: MediaSortColumn.DateDeleted,
                isSortable: true,
                name: MediaSortColumn.DateDeleted,
            },
        ];
    }

    let retCols: ISortableColumn[];
    if (mediaType === MediaType.Video) {
        retCols = fileSortableColumns.filter(
            (x) =>
                x.id !== MediaSortColumn.DateTaken &&
                (isInPortal({ builder: true }) || x.name !== MediaSortColumn.Uploaded),
        );
    } else {
        const hideUploadColumn = (name: string) => name !== MediaSortColumn.Uploaded;
        retCols = fileSortableColumns
            .filter((x) => x.id !== MediaSortColumn.DateTaken || mediaType === MediaType.Photo)
            .filter((x) => x.name !== MediaSortColumn.Duration)
            .filter((x) =>
                isInPortal({
                    builder: true,
                    subs: hideUploadColumn(x.name),
                    owner: hideUploadColumn(x.name),
                }),
            );
    }

    if (directoryType === DirectoryType.AttachedFiles) {
        return retCols;
    }
    return retCols.filter((x) => x.name !== MediaSortColumn.TotalSize);
};

export const applyFolderSort = (entry: MediaEntry, sort: GridSort) => {
    const folders = entry.folders?.sort((a, b) => {
        let aDate: Moment;
        let bDate: Moment;
        let result: number;
        switch (sort.sortColumn) {
            case MediaSortColumn.Name:
                result = lexicographicSorter(a.title, b.title);
                break;
            case MediaSortColumn.Modified:
                aDate = moment(a.dateModified);
                bDate = moment(b.dateModified);
                result = dateSorter(aDate, bDate);
                break;
            case MediaSortColumn.Created:
                aDate = moment(a.dateAdded);
                bDate = moment(b.dateAdded);
                result = dateSorter(aDate, bDate);
                break;
            case MediaSortColumn.Duration:
                return 0;
            default:
                return -1;
        }
        return sort.sortDirection === "asc" ? result : result * -1;
    });
    return folders ? [...folders] : null;
};

export const applyFileSort = (entry: MediaEntry, sort: GridSort) => {
    const files = entry.files?.sort((a, b) => {
        let aDate: Moment;
        let bDate: Moment;
        let result: number;
        switch (sort.sortColumn) {
            case MediaSortColumn.Name:
                result = lexicographicSorter(a.title, b.title);
                break;
            case MediaSortColumn.Modified:
                aDate = moment(a.dateModified);
                bDate = moment(b.dateModified);
                result = dateSorter(aDate, bDate);
                break;
            case MediaSortColumn.Uploaded:
            case MediaSortColumn.Created:
                aDate = moment(a.dateAttached);
                bDate = moment(b.dateAttached);
                result = dateSorter(aDate, bDate);
                break;
            case MediaSortColumn.DateDeleted:
                aDate = moment(a.dateDeleted);
                bDate = moment(b.dateDeleted);
                result = dateSorter(aDate, bDate);
                break;
            case MediaSortColumn.Duration:
                const aDuration = a.videoProperties?.length;
                const bDuration = b.videoProperties?.length;
                result = numberSorter(aDuration, bDuration);
                break;
            case MediaSortColumn.DateTaken:
                aDate = moment(a.dateTaken);
                bDate = moment(b.dateTaken);
                result = dateSorter(aDate, bDate);
                break;
            default:
                return -1;
        }
        return sort.sortDirection === "asc" ? result : result * -1;
    });
    return files ? [...files] : null;
};

export const getFolderMenuItemsVisibility = memoizeOne(
    (folder: BTFolder, isTemplateMode: boolean): IFolderMenuItemsVisibility => {
        const showFolderDetails = !folder.specialFolderExtraData.isSpecialFolder;
        const showEditFolderProperties =
            !folder.specialFolderExtraData.isSpecialFolder && folder.canUpdate;
        const showDownload = canSelectFolder(folder) && folder.mediaType !== MediaType.Video;
        const showCopy =
            !folder.specialFolderExtraData.isSpecialFolder &&
            folder.parentFolderId !== rootLevelFolderId &&
            folder.canUpdate;
        const showMove = showCopy && folder.mediaType !== MediaType.Video;
        const showDelete = !folder.specialFolderExtraData.isSpecialFolder && folder.canDelete;
        const showGenerateQRCode =
            isInPortal({ builder: true }) &&
            !folder.specialFolderExtraData.isSpecialFolder &&
            !isTemplateMode &&
            folder.directoryType !== DirectoryType.SubOwnerUploadedFiles;
        const showFolderSharing =
            isInPortal({ builder: true }) &&
            !isTemplateMode &&
            !folder.specialFolderExtraData.isSpecialFolder;
        const areSomeMenuItemsShown =
            showFolderDetails ||
            showEditFolderProperties ||
            showDownload ||
            showCopy ||
            showMove ||
            showDelete ||
            showGenerateQRCode ||
            showFolderSharing;

        return {
            showFolderDetails,
            showEditFolderProperties,
            showDownload,
            showCopy,
            showMove,
            showDelete,
            areSomeMenuItemsShown,
            showGenerateQRCode,
            showFolderSharing,
        } as IFolderMenuItemsVisibility;
    },
);

export const isPhotoAttachedFiles = (mediaType: MediaType, directoryType?: DirectoryType) =>
    mediaType === MediaType.Photo && directoryType === DirectoryType.AttachedFiles;
export const isPhotosOwnerAndSubPortal = (mediaType: MediaType) =>
    mediaType === MediaType.Photo && isInPortal({ owner: true, subs: true });

export const sortMediaListEntity = (entity: MediaListEntity, sortData?: IMediaListSort) => {
    const e = { ...entity };
    if (sortData) {
        const mediaEntry = e.mediaEntry;
        if (sortData.fileSort) {
            e.mediaEntry.files = applyFileSort(mediaEntry, sortData.fileSort);
        }
        if (sortData.folderSort) {
            e.mediaEntry.folders = applyFolderSort(mediaEntry, sortData.folderSort);
        }
    }
    return e;
};

/**
 * The sort applied on first visit (or when no valid saved sort exists) for a given context.
 * Photos in standard directories return undefined to preserve the manual drag ordering.
 * Documents and videos default to Modified desc, matching their existing hardcoded default.
 */
export const getDefaultMediaSort = (
    directoryType: DirectoryType,
    mediaType: MediaType,
): IMediaListSort | undefined => {
    switch (directoryType) {
        case DirectoryType.AttachedFiles:
        case DirectoryType.SubOwnerUploadedFiles:
            return { fileSort: { sortColumn: MediaSortColumn.Modified, sortDirection: "desc" } };
        case DirectoryType.Trash:
            return {
                fileSort: { sortColumn: MediaSortColumn.DateDeleted, sortDirection: "desc" },
            };
        default:
            if (mediaType === MediaType.Photo) {
                return undefined;
            }
            return { fileSort: { sortColumn: MediaSortColumn.Modified, sortDirection: "desc" } };
    }
};

/**
 * Drops any file/folder sort whose column isn't selectable in the given context. This guards against
 * applying a persisted or default sort that is invalid here (e.g. a Trash "Date Deleted" sort restored
 * in a Standard folder), which would otherwise produce a degenerate sort and a phantom selection in
 * the sort control.
 */
export const getValidatedMediaSort = (
    sort: IMediaListSort | undefined,
    directoryType: DirectoryType,
    mediaType: MediaType,
): IMediaListSort | undefined => {
    if (!sort) {
        return undefined;
    }

    const isValidColumn = (columns: ISortableColumn[], sortColumn?: string) =>
        !!sortColumn && columns.some((column) => column.name === sortColumn);

    const fileSort = isValidColumn(
        getFileSortableColumns(directoryType, mediaType),
        sort.fileSort?.sortColumn,
    )
        ? sort.fileSort
        : undefined;
    const folderSort = isValidColumn(
        getFolderSortableColumns(directoryType),
        sort.folderSort?.sortColumn,
    )
        ? sort.folderSort
        : undefined;

    if (!fileSort && !folderSort) {
        return undefined;
    }
    return { fileSort, folderSort };
};

/**
 * Resolves the sort to apply on load: the user's saved sort for this context if it is still valid,
 * otherwise the per-directory default. Both candidates are validated against the current context.
 */
export const getEffectiveMediaSort = (
    savedSort: IMediaListSort | undefined,
    directoryType: DirectoryType,
    mediaType: MediaType,
): IMediaListSort | undefined => {
    return (
        getValidatedMediaSort(savedSort, directoryType, mediaType) ??
        getDefaultMediaSort(directoryType, mediaType)
    );
};

export const getAllSupportedFileTypes = () => {
    return [
        ...documentFileTypes,
        ...photoFileTypes,
        ...videoFileTypes,
        ...SoftPlanSupportedFileTypes,
    ];
};

export const convertBTFileSystemToBTServiceFileUpload = (
    file: BTFileSystem,
): BTServiceFileUpload => {
    const btFile: BTServiceFileUpload = {
        // file.id is typed as required but partial mock data and AttachmentContainer
        // passive effects can hand us a record where id was never populated; coerce
        // to "" so the upstream form state stays a string instead of crashing.
        id: file.id?.toString() ?? "",
        tempId: file.tempId,
        uniqueId: file.uniqueId,
        ext: file.extension,
        fileName: file.title,
        fileSize: file.fileSize,
        hasChanged: false,
        isOriginalResolution: file.isOriginalResolution ?? false,
        attachAnnotationLayers: [],
        deleteAnnotationLayers: [],
        breakLinks: false,
        is360Media: file.is360Media,
        notifications: {
            showOwner: file.viewingPermissions?.showOwner ?? false,
            showSubs: file.viewingPermissions?.showSubs ?? false,
            mainFile: file.viewingPermissions?.mainFile ?? false,
            notifyBuilder: false,
            notifyOwner: false,
            notifySubs: false,
        },
        viewingPermissions: {
            showOwner: file.viewingPermissions?.showOwner ?? false,
            showSubs: file.viewingPermissions?.showSubs ?? false,
            mainFile: file.viewingPermissions?.mainFile ?? false,
        },
        dateTaken: file.dateTaken ? moment(file.dateTaken).toDate() : undefined,
        viewingOrder: file.viewingOrder,
    };
    return btFile;
};

/**
 * Removes files picked from the existing BT file system whose source job does not
 * match the target job. Does not work for BT Files on existing entities. That requires more rework.
 */
export const filterAttachedFilesForJob = (
    attachedFiles: AttachedFiles,
    jobId: number,
): AttachedFiles => {
    return {
        ...attachedFiles,
        files: attachedFiles.files.filter(
            (f) =>
                !f.existingBTDoc || f.jobsiteId === jobId || f.jobsiteId === JobIdTypes.GlobalDocs,
        ),
    };
};

export const convertFilesToAttachedFilesRequest = (files: BTFileSystem[]): AttachedFilesRequest => {
    return new AttachedFilesRequest({
        attachDocs: files.map((file) => ({
            ...convertBTFileSystemToBTServiceFileUpload(file),
            uniqueId: file.isTempFile ? undefined : file.id,
        })),
    });
};

export const convertBTFileToBTServiceFileUpload = (file: BTFile): BTServiceFileUpload => {
    const btFile: BTServiceFileUpload = {
        id: file.id.toString(),
        tempId: 0,
        uniqueId: file.id,
        annotationTempFileId: 0,
        annotationGroupId: file.annotations?.annotationGroupId,
        ext: file.extension,
        fileName: file.title,
        fileSize: file.sizeInBytes,
        hasChanged: false,
        isOriginalResolution: false,
        attachAnnotationLayers: [],
        deleteAnnotationLayers: [],
        breakLinks: false,
        is360Media: file.photoProperties?.is360Media ?? false,
        notifications: {
            showOwner: true,
            showSubs: true,
            notifyOwner: false,
            notifySubs: false,
            notifyBuilder: false,
            mainFile: false,
        },
        viewingPermissions: {
            showOwner: true,
            showSubs: true,
            mainFile: false,
        },
        dateTaken: file.dateTaken ? moment(file.dateTaken).toDate() : undefined,
    };
    return btFile;
};

export const SoftPlanSupportedFileTypes = [".cdf", ".csv", ".txt"];

export const TableDrawerXSOn = 18;
export const TableDrawerXSOff = 24;
export const DrawerXSOn = 6;
export const DrawerXSOff = 0;

export const folderIdsExcludedFromFiltering = [
    rootLevelFolderId,
    trashFolderId,
    attachedDocsFolderId,
    attachedPhotosFolderId,
    attachedVideosFolderId,
    ownerDocumentsFolderId,
    subUploadFolders,
];

/**
 * This method is used specifically for building a unique route for the legacy CFV to open the File Viewer. (Media
 * Properties)
 */
export function buildAttachedMediaPropertiesRouting(
    {
        fileId,
        jobId,
        mediaType,
        activeTab,
    }: {
        fileId: number;
        jobId: number | null;
        mediaType: MediaType;
        activeTab?: MediaPropertiesTab;
    },
    entityName: string,
    entityId: number,
): string {
    return `/Attachment/${entityName}/${entityId}${routes.media.getMediaPropertiesLink(
        fileId,
        mediaType,
        jobId ?? 0,
        0,
        TemplateType.None,
        0,
        activeTab,
    )}`;
}

export function canOpenMediaProperties(
    isTempFile: boolean,
    entityId: number,
    isOnExternalPage: boolean,
) {
    return !isTempFile && entityId !== 0 && !isOnExternalPage;
}

export function getSignatureRequestStatusMessage(
    signatureRequestStatus: SignatureRequestStatus,
    canSign: boolean,
) {
    if (canSign) {
        return needsYourSignature;
    }

    if (signatureRequestStatus === SignatureRequestStatus.Complete) {
        return "Signatures complete";
    } else {
        return "Signatures pending";
    }
}

export function getSignatureRequestStatusBadge(
    signatureRequestStatus: SignatureRequestStatus,
    canSign: boolean,
) {
    if (canSign) {
        return "danger";
    }

    if (signatureRequestStatus === SignatureRequestStatus.Complete) {
        return "success";
    } else {
        return "warning";
    }
}

export function canClickFile(file: BTFileSystem, entityId: number) {
    return (
        canViewBTFileSystemInLightbox(file) ||
        (file.mediaType !== MediaType.Video && !file.isTempFile && entityId !== 0)
    );
}

export function getLightboxThumbnailUrl(file: BTFileSystem) {
    const thumbnail = file.thumbnail ?? file.docPath;
    return canViewBTFileSystemInLightbox(file) ||
        (file.mediaType === MediaType.Video && file.fileStatus === FileStatus.Error) ||
        (file.mediaType === MediaType.Video && !!file.thumbnail)
        ? thumbnail
        : undefined;
}

export function getDocumentThumbnailUrl(file: BTFileSystem) {
    return file.mediaType === MediaType.Document && file.thumbnail !== undefined
        ? file.thumbnail
        : undefined;
}

/**
 * Generic utility function for sorting media objects (BTFileSystem or Lightbox slides)
 * by main file status and date attached.
 *
 * Main files appear first in the list, then files are sorted by date in descending order (newest first).
 *
 * @param a First object to compare
 * @param b Second object to compare
 * @returns Sort order value: negative if a comes first, positive if b comes first
 */
export function sortMediaByMainFileAndDateAttached<
    T extends {
        viewingPermissions?: { mainFile?: boolean } | null;
        dateAttached?: Moment | null;
        metadata?: { mainFile?: boolean; dateAttached?: Moment };
    },
>(a: T, b: T): number {
    // Check for main file status first
    const aIsMainFile = a.viewingPermissions?.mainFile || a.metadata?.mainFile;
    const bIsMainFile = b.viewingPermissions?.mainFile || b.metadata?.mainFile;

    if (bIsMainFile !== aIsMainFile) {
        return bIsMainFile ? 1 : -1;
    }

    // Then sort by date attached (newest first)
    const aDate = a.dateAttached?.valueOf() ?? a.metadata?.dateAttached?.valueOf() ?? 0;
    const bDate = b.dateAttached?.valueOf() ?? b.metadata?.dateAttached?.valueOf() ?? 0;

    return bDate - aDate;
}

export function canMultiSelectFiles(
    files: BTFileSystem[],
    isOnExternalPage: boolean,
    isReadOnly?: boolean,
) {
    return (
        isOnExternalPage === false &&
        isReadOnly === false &&
        files.length > 0 &&
        files.some((file) => file.permissions?.canDelete)
    );
}

export function hasSelectedFiles(selectedFileIds?: number[]) {
    return !!selectedFileIds?.length;
}

export function areAllFilesSelected(files: BTFileSystem[], selectedFileIds?: number[]) {
    if (files.length === 0 || !selectedFileIds?.length) {
        return false;
    }
    // Only consider files with canDelete permission when checking if all files are selected
    const deletableFiles = files.filter((file) => file.permissions?.canDelete);
    if (deletableFiles.length === 0) {
        return false;
    }
    return deletableFiles.every((file) => selectedFileIds.includes(file.id));
}

export function handleSelectAllFiles(
    files: BTFileSystem[],
    selectedFileIds?: number[],
    onSelectedFileChange?: (fileId: number, isSelected: boolean) => void,
): void {
    if (!selectedFileIds || !onSelectedFileChange) {
        return;
    }

    if (areAllFilesSelected(files, selectedFileIds)) {
        selectedFileIds?.forEach((id) => onSelectedFileChange(id, false));
    } else {
        // Filter files to only those with canDelete permission
        const deletableFiles = files.filter((file) => file.permissions?.canDelete);
        deletableFiles.forEach((file) => {
            if (!selectedFileIds?.includes(file.id)) {
                onSelectedFileChange(file.id, true);
            }
        });
    }
}

const getShareFileLink = async (
    id: number,
    request: IGetShareTokenRequest,
    fileShareHandler: IFileShareHandler,
): Promise<string> => {
    return (await fileShareHandler.GetShareToken(id, request)).shareContentUrl;
};

export const writeShareLinkToClipboard = async (
    fileShareHandler: IFileShareHandler | undefined,
    entityId: number,
    jobId: number,
) => {
    const request: IGetShareTokenRequest = { jobId: jobId, shareType: ShareType.Entity };
    if (fileShareHandler) {
        await writeToClipboard(
            getShareFileLink(entityId, request, fileShareHandler),
            "Share link copied",
        );
    }
};

export const writeToClipboard = async (func: Promise<string>, successMsg: string) => {
    await navigator.clipboard
        // Safari requires clipboard.write() to be called synchronously within the user gesture's call stack.
        // Since await breaks this chain, we must pass unresolved Promises directly to ClipboardItem
        // see: https://stackoverflow.com/questions/66312944/javascript-clipboard-api-write-does-not-work-in-safari
        .write([
            new ClipboardItem({
                "text/plain": func,
            }),
        ])
        .then(() => {
            void message.success(successMsg);
        })
        .catch((e) => {
            showAPIErrorMessage(e);
        });
};

const getHtmlForImageCopy = async (file: BTFile) => {
    // We want to limit the size of data being copied.  We are only going to specify a width
    // which will allow the image to scale appropriately
    // note: does not scale up if width is greater than actual width of photo
    const imageWidth = 800;
    const qsParams = queryString.parseUrl(file.thumbnail);
    qsParams.query.width = String(imageWidth);
    qsParams.query.autorotate = String("true");

    const base64String = await getBase64ImageFromUrl(queryString.stringifyUrl(qsParams));
    await validateBase64Image(base64String);

    return `<img src="${base64String}" width="${imageWidth}" alt="${file.title}" />`;
};

const getBlobFromHtmlForImageCopy = async (file: BTFile) => {
    return new Blob([await getHtmlForImageCopy(file)], { type: "text/html" });
};

export const copyImageToClipboard = async (file: BTFile) => {
    await navigator.clipboard
        .write([
            new ClipboardItem({
                "text/html": getBlobFromHtmlForImageCopy(file),
            }),
        ])
        .then(() => {
            void message.success("Image copied to clipboard!");
        })
        .catch((e) => {
            showAPIErrorMessage(e);
        });
};

const getHtmlForImageCopyFromEntity = async (entity: IMediaPropertiesEntity) => {
    if (!entity.photoUrl) {
        throw new Error("Entity does not have a photoUrl");
    }
    const imageWidth = 800;
    const qsParams = queryString.parseUrl(entity.photoUrl);
    qsParams.query.width = String(imageWidth);
    qsParams.query.autorotate = String("true");

    const base64String = await getBase64ImageFromUrl(queryString.stringifyUrl(qsParams));
    await validateBase64Image(base64String);

    return `<img src="${base64String}" width="${imageWidth}" alt="${entity.title}" />`;
};

const getBlobFromHtmlForImageCopyFromEntity = async (entity: IMediaPropertiesEntity) => {
    return new Blob([await getHtmlForImageCopyFromEntity(entity)], { type: "text/html" });
};

/**
 * Copies an image from a MediaPropertiesEntity to the clipboard.
 * This version is specifically for use with IMediaPropertiesEntity (used in MediaPropertiesHeaderContent).
 */
export const copyImageToClipboardFromEntity = async (entity: IMediaPropertiesEntity) => {
    await navigator.clipboard
        .write([
            new ClipboardItem({
                "text/html": getBlobFromHtmlForImageCopyFromEntity(entity),
            }),
        ])
        .then(() => {
            void message.success("Image copied to clipboard!");
        })
        .catch((e) => {
            showAPIErrorMessage(e);
        });
};

/**
 * Returns true if the current URL already represents the root media route, meaning no redirect is needed.
 *
 * The plain `includes` check fails for webview portal URLs (e.g. /subs/webview/ios/10.76.0/Photos/Standard/0)
 * because the webview-specific segments sit between the portal prefix and the page path, so
 * newRoute (/subs/Photos/Standard/0) is not a substring of matchUrl. Stripping the portal prefix
 * before the check resolves this — /Photos/Standard/0 is present in both URL forms.
 */
export function isUrlAtRootMediaRoute(matchUrl: string, newRoute: string): boolean {
    const portalPrefix = addPathForCurrentPortal("");
    const coreRoutePath =
        portalPrefix && newRoute.startsWith(portalPrefix)
            ? newRoute.slice(portalPrefix.length)
            : newRoute;
    return matchUrl.includes(coreRoutePath);
}
